import { z } from 'zod';
import type { QueryState } from '@/services/nlq/nlq-service';
import { buildQuerySchema, BuildQueryContext, QueryResult } from './types';
import { executeNlqV3Query } from '../shared/nlq-query-helpers';
import { TOOL_NAME_BUILD_QUERY, toEngineName } from '@/constants';
import { devDebug } from '@/utils/dev-debug';

/**
 * Standalone build query engine - completely decoupled from any UI or store
 * Converts a natural language query into an NLQ result and dataset
 * @param params - The parameters containing data source title and query prompt
 * @param context - Context with tool call ID and optional httpClient
 * @returns The query result with queryId, nlqResult, and dataset
 */
export async function buildQueryEngine(
	params: z.infer<typeof buildQuerySchema>,
	context: BuildQueryContext,
): Promise<QueryResult> {
	const { dataSourceTitle, queryPrompt, priorQueryId } = params;

	devDebug('engineCall', {
		engine: toEngineName(TOOL_NAME_BUILD_QUERY),
		dataSourceTitle,
		queryPrompt,
		priorQueryId,
		toolCallId: context.toolCallId,
	});

	let priorQueryState: QueryState | undefined;
	if (priorQueryId != null && priorQueryId !== '') {
		if (!context.retrieveQuery) {
			throw new Error(
				'Error: priorQueryId was set but retrieveQuery is missing from BuildQueryContext. ' +
					'Wire retrieveQuery so the engine can load nlqResult.queryState for incremental NLQ.',
			);
		}
		const priorResult = context.retrieveQuery(priorQueryId);
		if (!priorResult) {
			throw new Error(
				`Error: Prior query not found for priorQueryId "${priorQueryId}". ` +
					'Pass a valid queryId from a prior buildQuery result.',
			);
		}
		priorQueryState = priorResult.nlqResult.queryState;
	}

	const isNlqDeltaEnabled = context.isNlqDeltaEnabled !== false;

	const nlqResult = await executeNlqV3Query(
		dataSourceTitle,
		queryPrompt,
		context.httpClient,
		context.commonFilters,
		priorQueryState,
		isNlqDeltaEnabled,
	);

	if (!nlqResult?.queryState) {
		throw new Error(
			`Error: Failed to build query for "${queryPrompt}". Query state not found. Try rephrasing or check data source "${dataSourceTitle}".`,
		);
	}

	if (!nlqResult.dataset) {
		nlqResult.dataset =
			'There was an error executing the query, please rephrase it or simplify it into multiple queries';
	}

	// use the tool call id as the query id for consistency with other engines
	const queryId = context.toolCallId;
	const title = nlqResult.queryState.title ?? '';

	return {
		queryId,
		title,
		nlqResult,
	};
}
